import { Base, Component, Emit, Prop, Watch } from '@/vue-property-decorator'
import { CommonCodeAuth } from '@/components/business-components'
import {
  CreateModeOptType,
  NormalLoginFormData,
  UserInfos
} from '@/api/common/type'
import { FormModel, Space } from '@/plugins/ant-design-vue/tsx-support'
import { GeeTest } from '@/controller/GeeTest'
import {
  GeetCaptProps,
  IRefs,
  LREvents,
  LoginFormState,
  LoginFromProps,
  LoginRegisterName,
  LoginRegisterValue,
  Themes
} from './types'
import { GlobalConst, GlobalEvents } from '@/context'
import { getCurrentDevice } from '@/utils/Device'
import { merge } from 'lodash'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import FuncRender from '@/components/business-components/func-render'
import MyScroll from '@/components/my-scroll'
import Rules from '@/controller/Rules'
import User from '@/controller/User'
import style from './form.module.scss'
const device = getCurrentDevice()

@Component<LoginFrom>({
  name: 'LoginFrom'
})
export default class LoginFrom extends Base<unknown, LoginFromProps, LREvents> {
  @Prop()
  private showLabel!: LoginFromProps['showLabel']
  @Prop()
  private type!: LoginFromProps['type']
  @Prop()
  private currentPhoneNumberSegment!: LoginFromProps['currentPhoneNumberSegment']

  private funcRender = FuncRender.createFormModelItem.call(this)
  private loginLoading = false
  private keyupCacheUsernameKeys = ''

  form: LoginFormState = {
    username: '',
    userpass: '',
    phone: '',
    email: '',
    phoneCode: '',
    emailCode: ''
  }

  created() {
    const { isTryMode, userInfos } = useMainStore()
    if (userInfos?.platfromid && !isTryMode) {
      this.form.username = userInfos?.platfromid
    }
  }

  mounted() {
    const { autoFillData, getAutoFillData } = useMainStore()
    if (autoFillData) {
      const formData = getAutoFillData()
      if (formData?.username) {
        this.form.username = formData.username
      }
      if (formData?.userpass) {
        this.form.userpass = formData.userpass
      }
      if (formData?.email) {
        this.form.email = formData.email
      }
      if (formData?.phone) {
        this.form.phone = formData.phone.split('-').pop() ?? ''
      }
    }
  }

  @Emit('currentPhoneNumberSegment')
  private onCurrentPhoneNumberSegment(currentPhoneNumberSegment: string) {
    return currentPhoneNumberSegment
  }
  @Emit('loginSuccess')
  private onLoginSuccess() {
    return this.form
  }
  @Emit('needRegister')
  private onNeedRegister(registrable: UserInfos['registrable']) {
    return { form: this.form, registrable }
  }
  @Emit('veriryBindPhone')
  private onVeriryBindPhone() {
    return this.form
  }
  @Watch('loginLoading')
  @Emit('loading')
  private onLoading() {
    return this.loginLoading
  }

  private get formatPhoneNumber() {
    if (this.form.phone && this.currentPhoneNumberSegment) {
      return `${this.currentPhoneNumberSegment}-${this.form.phone}`
    }
    return ''
  }

  public get formInstance() {
    return (this.$refs as unknown as IRefs)[this.type]
  }

  private get layout() {
    if (!this.showLabel) return {}
    return {
      layout: 'horizontal',
      labelCol: { span: 5 },
      wrapperCol: { span: 19 }
    }
  }

  private get placeholder(): LoginFormState {
    const { homeLayoutInfos } = useMainStore()
    const { t } = useI18n()
    switch (Number(homeLayoutInfos?.commonConfig?.registerLogin)) {
      case Themes.Theme3:
        return {
          username: String(
            t('lobby.modal.login.placeholder.platformId3', { minMax: '4-16' })
          ),
          userpass: String(
            t('lobby.modal.login.placeholder.passwd3', { minMax: '4-16' })
          ),
          phone: String(t('lobby.modal.login.placeholder.mobilePhoneNumber')),
          phoneCode: String(
            t('lobby.modal.login.placeholder.verificationCode3')
          ),
          email: String(t('lobby.modal.login.placeholder.email')),
          emailCode: String(t('lobby.modal.login.placeholder.emailCode3'))
        }
      case Themes.Theme2:
      case Themes.Theme1:
      default:
        return {
          username: String(t('lobby.modal.login.placeholder.platformId')),
          userpass: String(t('lobby.modal.login.placeholder.passwd')),
          phone: String(t('lobby.modal.login.placeholder.mobilePhoneNumber')),
          phoneCode: String(
            t('lobby.modal.login.placeholder.verificationCode')
          ),
          email: String(t('lobby.modal.login.placeholder.email')),
          emailCode: String(t('lobby.modal.login.placeholder.emailCode'))
        }
    }
  }

  private formatPayload() {
    switch (this.type) {
      case LoginRegisterName.Account:
        return {
          username: this.form.username,
          userpass: this.form.userpass,
          loginType: LoginRegisterValue.Account
        }
      case LoginRegisterName.Phone:
        return {
          phone: this.formatPhoneNumber,
          captcha: this.form.phoneCode,
          loginType: LoginRegisterValue.Phone
        }
      case LoginRegisterName.Email:
      default:
        return {
          email: this.form.email,
          captcha: this.form.emailCode,
          loginType: LoginRegisterValue.Email
        }
    }
  }

  /** 极验 */
  private async loginValidate({ code, geetCaptchaId }: GeetCaptProps) {
    const hasGeetTest = GlobalConst.ServiceCode.FORCE_GEETEST === code
    if (hasGeetTest || geetCaptchaId) {
      if (GeeTest.checkGeetestRestricted()) {
        return Promise.reject()
      }
      const { systemInfos, uuid } = useMainStore()
      const captchaId =
        geetCaptchaId ??
        (hasGeetTest
          ? systemInfos?.geetest_captcha_id.force_geetest
          : systemInfos?.geetest_captcha_id.login) ??
        ''
      const result = await GeeTest.initGeetest({
        geetestConfig: { captchaId: captchaId }
      })

      if (result) {
        let account = uuid
        if (this.type === LoginRegisterName.Account) {
          account = this.form.username
        }
        if (this.type === LoginRegisterName.Phone) {
          account = this.formatPhoneNumber
        }
        if (this.type === LoginRegisterName.Email) {
          account = this.form.email
        }
        await GeeTest.validateGeetest({
          account,
          ...merge(result, {
            captcha_id: result?.captcha_id || captchaId
          })
        })
      }
    }

    /** 登入短信行为验证 */
    if (code === GlobalConst.ServiceCode.SMS_CODE_GEETEST) {
      this.onVeriryBindPhone()
      return Promise.reject()
    }
  }

  private get isH5Pwa() {
    return device.isH5Pwa()
  }

  /** 获取当前运行环境是否是桌面快捷方式*/
  private get getisPwaParams() {
    const isPwa = this.isH5Pwa
    if (isPwa) {
      return { isPwa: 1 }
    }
    return {}
  }

  public async submit({ code, geetCaptchaId }: GeetCaptProps = {}) {
    const { updateAutoFillData } = useMainStore()

    if (this.loginLoading) return
    await this.formInstance.validate()
    this.loginLoading = true

    const payload = this.formatPayload()
    try {
      await this.loginValidate({ code, geetCaptchaId })
      const userInfo = await User.login({
        ...payload,
        ...this.getisPwaParams
      } as NormalLoginFormData)
      setTimeout(() => (this.loginLoading = false))

      /** 新注册用户，去设置账号 */
      if (userInfo?.registrable && this.type !== LoginRegisterName.Account) {
        this.onNeedRegister(userInfo?.registrable)
        return
      }

      /** 正常登录成功 */
      this.onLoginSuccess()
      updateAutoFillData({
        username: payload.username,
        userpass: payload.userpass,
        email: payload.email,
        phone: payload.phone,
        phoneNumberSegment: this.currentPhoneNumberSegment
      })
    } catch (error) {
      this.loginLoading = false
      const err = error as { data: { code: number | undefined; msg: string } }
      const code = err?.data?.code
      const msg = err?.data?.msg
      const serviceCode = GlobalConst.ServiceCode
      /** 如果返回code等于拼图、图标、九宫格行为验证，就把captchaId传进去 */
      if (
        (code === serviceCode.SWIPER_PUZZLE_GEETEST ||
          code === serviceCode.NINE_SQUARE_GEETEST ||
          code === serviceCode.GRAPHIC_CLICK_GEETEST) &&
        msg
      ) {
        this.submit({ code, geetCaptchaId: msg })
      }

      if (
        code === serviceCode.SMS_CODE_GEETEST ||
        code === serviceCode.FORCE_GEETEST
      ) {
        this.submit({ code })
      }
    }
  }

  private onUsernameBlur() {
    setTimeout(() => {
      if (!this.form.username) {
        GlobalEvents.dispatch({
          type: 'TRACE_REPORT',
          payload: {
            event: 'loginUsername',
            keyupCacheUsername: this.keyupCacheUsernameKeys
          }
        })
      }
      this.keyupCacheUsernameKeys = ''
    }, 2000)
  }

  private onUsernameKeyup(e: KeyboardEvent) {
    this.keyupCacheUsernameKeys += e.key
  }

  private fromItems() {
    const { t } = useI18n()
    const mainStore = useMainStore()
    const { account, email, phone } = {
      [LoginRegisterName.Account]: { [LoginRegisterName.Account]: true },
      [LoginRegisterName.Phone]: { [LoginRegisterName.Phone]: true },
      [LoginRegisterName.Email]: { [LoginRegisterName.Email]: true },
      [LoginRegisterName.ThirdParty]: { [LoginRegisterName.ThirdParty]: true }
    }[this.type]

    return (
      <Space.Tsx
        direction="vertical"
        size={0}
        class={'login-register-from-scroll'}
      >
        {[
          Boolean(account) &&
            this.funcRender.username({
              prop: 'username',
              label: t('lobby.modal.login.form.username.labelShort'),
              isRequired: !this.form.username,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.username
                },
                on: {
                  blur: this.onUsernameBlur,
                  keyup: this.onUsernameKeyup
                }
              },
              beforeChange: (v) => v.replace(/\s/g, '')
            }),
          Boolean(account) &&
            this.funcRender.userpass({
              prop: 'userpass',
              isRequired: !this.form.userpass,
              inputTsxData: {
                attrs: {
                  autocomplete: 'new-password',
                  placeholder: this.placeholder.userpass
                }
              },
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              beforeChange: (v) => v.replace(/\s/g, ''),
              getRules(rules) {
                return [rules.required, Rules.userpass({ start: 6, end: 16 })]
              }
            }),

          Boolean(phone) &&
            this.funcRender.userPhoneSelect({
              prop: 'phone',
              isRequired: !this.form.phone,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              model: this.form,
              countryInfos: mainStore.countryInfos,
              defaultCountry: mainStore.defaultCountry ?? undefined,
              currentPhoneNumberSegment: this.currentPhoneNumberSegment,
              inputTsxData: {
                attrs: {
                  placeholder: this.placeholder.phone
                }
              },
              onSelectChange: (value: string) => {
                this.onCurrentPhoneNumberSegment(value)
                this.form.phone = ''
              }
            }),
          Boolean(phone) && (
            <CommonCodeAuth
              prop={'phoneCode'}
              mode="sms"
              createMode={{ sms: CreateModeOptType.LoginRegister }}
              isRequired={true}
              placeholder={this.placeholder.phoneCode}
              showRequiredLabel={!this.form.phoneCode}
              showLabel={this.showLabel}
              hiddenPrefix={this.showLabel}
              hiddenIcon={this.showLabel}
              targetValue={this.formatPhoneNumber}
              v-model={this.form.phoneCode}
            />
          ),
          Boolean(email) &&
            this.funcRender.useremail({
              prop: 'email',
              atuoLowerCase: false,
              isRequired: !this.form.email,
              showLabel: this.showLabel,
              hiddenPrefix: this.showLabel,
              hiddenIcon: this.showLabel,
              selectTsxData: { placeholder: this.placeholder.email },
              model: this.form
            }),
          Boolean(email) && (
            <CommonCodeAuth
              prop={'emailCode'}
              createMode={{ email: CreateModeOptType.LoginRegister }}
              isRequired={true}
              showRequiredLabel={!this.form.emailCode}
              showLabel={this.showLabel}
              hiddenPrefix={this.showLabel}
              hiddenIcon={this.showLabel}
              targetValue={this.form.email}
              placeholder={this.placeholder.emailCode}
              mode={'email'}
              v-model={this.form.emailCode}
            />
          )
        ]}
      </Space.Tsx>
    )
  }

  render() {
    return (
      <FormModel.Tsx
        ref={this.type}
        props={{ model: this.form, ...this.layout }}
        class={style['login-register-from']}
      >
        <MyScroll>{this.fromItems()}</MyScroll>
      </FormModel.Tsx>
    )
  }
}
